/**
 * mSTEM Kit Edit Form (GEN-04). Inline form rendered inside the
 * KitManagerModal for both Add (initialKit=null) and Edit
 * (initialKit=existing) flows.
 *
 * Validation:
 *  - name trimmed; empty name aborts submit silently.
 *  - maxLength={100} on name input pre-truncates per T-02-04-01.
 *  - sensorCount / relayCount clamped to [0, 999] via Math.max/min — keeps
 *    the form impossible to overflow the reducer guard.
 *  - hasRFID stays boolean — the react-switch onChange callback receives
 *    a boolean from the lib, eliminating any string-coercion surface.
 *  - id stays stable on edit; for create, slugify(name) becomes the id.
 *  - firmwareVersion (SMH-04) is free text capped at 40 chars; blank on
 *    create so a hand-made kit shows "Chưa rõ" until a version is entered.
 *
 * SMH-04: the submitted kit spreads the existing entry first, so detail-only
 * metadata (model, MCU, MAC, IP, serial, lastSyncAt…) survives an edit even
 * though this form does not expose those fields.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import Switch from 'react-switch';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import styles from './kit-manager-modal.css';

const messages = defineMessages({
    labelName: {
        defaultMessage: 'Tên kit',
        description: 'Form label — kit name',
        id: 'gen.kit.form.label.name'
    },
    labelSensors: {
        defaultMessage: 'Số sensor',
        description: 'Form label — sensor count',
        id: 'gen.kit.form.label.sensors'
    },
    labelRelays: {
        defaultMessage: 'Số relay',
        description: 'Form label — relay count',
        id: 'gen.kit.form.label.relays'
    },
    labelRFID: {
        defaultMessage: 'Hỗ trợ RFID',
        description: 'Form label — RFID toggle',
        id: 'gen.kit.form.label.rfid'
    },
    labelFirmware: {
        defaultMessage: 'Phiên bản firmware',
        description: 'Form label — installed firmware version (SMH-04)',
        id: 'gen.kit.form.label.firmware'
    },
    save: {
        defaultMessage: 'Lưu',
        description: 'Button — save kit',
        id: 'gen.kit.form.save'
    },
    cancel: {
        defaultMessage: 'Huỷ',
        description: 'Button — cancel kit edit',
        id: 'gen.kit.form.cancel'
    }
});

const slugify = function (text) {
    return String(text || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 64) || `kit-${Date.now()}`;
};

const clampCount = function (raw) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(999, n));
};

class KitEditForm extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChangeName',
            'handleChangeSensors',
            'handleChangeRelays',
            'handleChangeRFID',
            'handleChangeFirmware',
            'handleSubmit',
            'handleCancel'
        ]);
        const k = props.initialKit || {};
        this.state = {
            name: k.name || '',
            sensorCount: typeof k.sensorCount === 'number' ? k.sensorCount : 0,
            relayCount: typeof k.relayCount === 'number' ? k.relayCount : 0,
            hasRFID: Boolean(k.hasRFID),
            firmwareVersion: k.firmwareVersion || ''
        };
    }
    handleChangeName (e) {
        this.setState({name: e.target.value});
    }
    handleChangeSensors (e) {
        this.setState({sensorCount: clampCount(e.target.value)});
    }
    handleChangeRelays (e) {
        this.setState({relayCount: clampCount(e.target.value)});
    }
    handleChangeRFID (checked) {
        this.setState({hasRFID: checked});
    }
    handleChangeFirmware (e) {
        this.setState({firmwareVersion: e.target.value});
    }
    handleSubmit (e) {
        if (e && e.preventDefault) e.preventDefault();
        const name = this.state.name.trim();
        if (!name) return;
        const existing = this.props.initialKit;
        // Spread the existing entry first so SMH-04 detail metadata this form
        // does not edit (model, MCU, MAC, serial…) is not silently dropped.
        const kit = Object.assign({}, existing || {}, {
            id: existing ? existing.id : slugify(name),
            name: name,
            sensorCount: this.state.sensorCount,
            relayCount: this.state.relayCount,
            hasRFID: this.state.hasRFID,
            firmwareVersion: this.state.firmwareVersion.trim(),
            image: existing ? existing.image : null,
            brand: existing ? existing.brand : 'MBF SmartHome'
        });
        this.props.onSave(kit);
    }
    handleCancel () {
        this.props.onCancel();
    }
    render () {
        return (
            <form
                className={styles.editForm}
                onSubmit={this.handleSubmit}
            >
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelName} />
                    </span>
                    <input
                        className={styles.formInput}
                        maxLength={100}
                        type="text"
                        value={this.state.name}
                        onChange={this.handleChangeName}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelSensors} />
                    </span>
                    <input
                        className={styles.formInput}
                        max={999}
                        min={0}
                        type="number"
                        value={this.state.sensorCount}
                        onChange={this.handleChangeSensors}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelRelays} />
                    </span>
                    <input
                        className={styles.formInput}
                        max={999}
                        min={0}
                        type="number"
                        value={this.state.relayCount}
                        onChange={this.handleChangeRelays}
                    />
                </label>
                <label className={styles.formField}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelFirmware} />
                    </span>
                    <input
                        className={styles.formInput}
                        maxLength={40}
                        placeholder="1.0.0"
                        type="text"
                        value={this.state.firmwareVersion}
                        onChange={this.handleChangeFirmware}
                    />
                </label>
                <label className={styles.formFieldInline}>
                    <span className={styles.formLabel}>
                        <FormattedMessage {...messages.labelRFID} />
                    </span>
                    <Switch
                        checked={this.state.hasRFID}
                        offColor="#989CAB"
                        onColor="#162A75"
                        onChange={this.handleChangeRFID}
                    />
                </label>
                <div className={styles.formActions}>
                    <button
                        className={styles.btnSecondary}
                        type="button"
                        onClick={this.handleCancel}
                    >
                        <FormattedMessage {...messages.cancel} />
                    </button>
                    <button
                        className={styles.btnPrimary}
                        type="submit"
                    >
                        <FormattedMessage {...messages.save} />
                    </button>
                </div>
            </form>
        );
    }
}

KitEditForm.propTypes = {
    initialKit: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        sensorCount: PropTypes.number,
        relayCount: PropTypes.number,
        hasRFID: PropTypes.bool,
        firmwareVersion: PropTypes.string,
        image: PropTypes.string,
        brand: PropTypes.string
    }),
    intl: intlShape.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default injectIntl(KitEditForm);
